/**
 * Firm Information Step Component
 * TASK-339: Onboarding Wizard UI
 *
 * Step 2: Collect firm details (optional)
 */

import React, { useState } from 'react';
import { Loader2, Building2, MapPin, Phone, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import { adminOnboardingService } from '../../../services/adminOnboardingService';
import { FirmInfoRequest } from '../../../types/adminOnboarding';
import {
  SWISS_LEGAL_FORMS,
  EMPLOYEE_COUNT_RANGES,
  INDUSTRY_SPECIALIZATIONS,
  SRO_MEMBERSHIPS,
} from '../../../constants/legalForms';

interface FirmInfoStepProps {
  token: string;
  companyName: string;
  onComplete: () => void;
  onSkip: () => void;
}

const FirmInfoStep: React.FC<FirmInfoStepProps> = ({
  token,
  companyName,
  onComplete,
  onSkip,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [activeSection, setActiveSection] = useState<'legal' | 'address' | 'contact' | 'business'>('legal');

  // Form state
  const [formData, setFormData] = useState<Partial<FirmInfoRequest>>({
    legal_company_name: companyName,
    address_country: 'CH',
  });

  const updateField = (field: keyof FirmInfoRequest, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await adminOnboardingService.saveFirmInfo({
        token,
        ...formData,
      });

      if (response.success) {
        toast.success('Firm information saved!');
        onComplete();
      } else {
        toast.error('Failed to save firm information');
      }
    } catch (err: any) {
      const message = err?.response?.data?.detail || 'Failed to save firm information';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Section tabs
  const sections = [
    { id: 'legal', label: 'Legal', icon: Building2 },
    { id: 'address', label: 'Address', icon: MapPin },
    { id: 'contact', label: 'Contact', icon: Phone },
    { id: 'business', label: 'Business', icon: Briefcase },
  ] as const;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Section tabs */}
      <div className="flex border-b border-gray-200">
        {sections.map(section => {
          const Icon = section.icon;
          return (
            <button
              key={section.id}
              type="button"
              onClick={() => setActiveSection(section.id)}
              className={`
                flex-1 flex items-center justify-center gap-2 py-3 px-4 text-sm font-medium
                border-b-2 transition-colors
                ${activeSection === section.id
                  ? 'border-kielis-navy text-kielis-navy'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
                }
              `}
            >
              <Icon size={16} />
              <span className="hidden sm:inline">{section.label}</span>
            </button>
          );
        })}
      </div>

      {/* Legal Information */}
      {activeSection === 'legal' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Legal Company Name
            </label>
            <input
              type="text"
              value={formData.legal_company_name || ''}
              onChange={(e) => updateField('legal_company_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kielis-gold focus:border-transparent"
              placeholder="Official registered company name"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Legal Form
              </label>
              <select
                value={formData.legal_form || ''}
                onChange={(e) => updateField('legal_form', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kielis-gold focus:border-transparent"
              >
                <option value="">Select legal form</option>
                {SWISS_LEGAL_FORMS.map(form => (
                  <option key={form.value} value={form.value}>{form.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Registration Number
              </label>
              <input
                type="text"
                value={formData.registration_number || ''}
                onChange={(e) => updateField('registration_number', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kielis-gold focus:border-transparent"
                placeholder="CHE-123.456.789"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                LEI Code
              </label>
              <input
                type="text"
                value={formData.lei_code || ''}
                onChange={(e) => updateField('lei_code', e.target.value.toUpperCase())}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kielis-gold focus:border-transparent"
                placeholder="20 character LEI"
                maxLength={20}
              />
              <p className="mt-1 text-xs text-gray-400">Legal Entity Identifier (optional)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                FINMA License
              </label>
              <input
                type="text"
                value={formData.finma_license_number || ''}
                onChange={(e) => updateField('finma_license_number', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kielis-gold focus:border-transparent"
                placeholder="If applicable"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              SRO Membership
            </label>
            <select
              value={formData.sro_membership || ''}
              onChange={(e) => updateField('sro_membership', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kielis-gold focus:border-transparent"
            >
              <option value="">Select SRO</option>
              {SRO_MEMBERSHIPS.map(sro => (
                <option key={sro.value} value={sro.value}>{sro.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Address */}
      {activeSection === 'address' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Street Address
            </label>
            <input
              type="text"
              value={formData.address_street || ''}
              onChange={(e) => updateField('address_street', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kielis-gold focus:border-transparent"
              placeholder="Street and number"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Postal Code
              </label>
              <input
                type="text"
                value={formData.address_postal_code || ''}
                onChange={(e) => updateField('address_postal_code', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kielis-gold focus:border-transparent"
                placeholder="8001"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                type="text"
                value={formData.address_city || ''}
                onChange={(e) => updateField('address_city', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kielis-gold focus:border-transparent"
                placeholder="Zurich"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Country
            </label>
            <select
              value={formData.address_country || 'CH'}
              onChange={(e) => updateField('address_country', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kielis-gold focus:border-transparent"
            >
              <option value="CH">Switzerland</option>
              <option value="LI">Liechtenstein</option>
              <option value="DE">Germany</option>
              <option value="AT">Austria</option>
              <option value="FR">France</option>
              <option value="IT">Italy</option>
            </select>
          </div>
        </div>
      )}

      {/* Contact */}
      {activeSection === 'contact' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Main Phone
            </label>
            <input
              type="tel"
              value={formData.main_phone || ''}
              onChange={(e) => updateField('main_phone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kielis-gold focus:border-transparent"
              placeholder="+41 44 123 45 67"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Website
            </label>
            <input
              type="url"
              value={formData.website_url || ''}
              onChange={(e) => updateField('website_url', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kielis-gold focus:border-transparent"
              placeholder="https://www.example.ch"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Billing Email
            </label>
            <input
              type="email"
              value={formData.billing_email || ''}
              onChange={(e) => updateField('billing_email', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kielis-gold focus:border-transparent"
              placeholder="billing@example.ch"
            />
            <p className="mt-1 text-xs text-gray-400">For invoices and billing communications</p>
          </div>
        </div>
      )}

      {/* Business */}
      {activeSection === 'business' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Industry Specialization
            </label>
            <select
              value={formData.industry_specialization || ''}
              onChange={(e) => updateField('industry_specialization', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kielis-gold focus:border-transparent"
            >
              <option value="">Select specialization</option>
              {INDUSTRY_SPECIALIZATIONS.map(spec => (
                <option key={spec.value} value={spec.value}>{spec.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Employee Count
            </label>
            <select
              value={formData.employee_count_range || ''}
              onChange={(e) => updateField('employee_count_range', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kielis-gold focus:border-transparent"
            >
              <option value="">Select range</option>
              {EMPLOYEE_COUNT_RANGES.map(range => (
                <option key={range.value} value={range.value}>{range.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-3 pt-4">
        <button
          type="button"
          onClick={onSkip}
          className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
        >
          Skip
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="flex-1 py-3 px-4 bg-kielis-navy text-white font-medium rounded-lg hover:bg-opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Saving...
            </>
          ) : (
            'Save & Continue'
          )}
        </button>
      </div>
    </form>
  );
};

export default FirmInfoStep;
